<template>
    <div :id="`answer-${index}`"
        :class="[
            'answer',
            {
                'answer--is-disabled': isPollDisabled,
                'answer--is-hoverable': canVote && !isOverlayVisible,
                'answer--is-voted': ['processing', 'accepted'].includes(answer.voteStatus)
            }
        ]"
    >
        <div :class="['answer__overlay', { 'answer__overlay--is-visible': isOverlayVisible || isVoteProcessing }]">
            <div v-show="!isVoteProcessing"
                :id="`cancel-vote-${index}`"
                class="answer__overlay-option answer__overlay-option--cancel"
            >
                <i class="icon icon-cancel-circle" /> {{ $t('pollDetails.cancel') }}
            </div>

            <div v-show="!isVoteProcessing"
                class="answer__overlay-option answer__overlay-option--vote"
                @click="vote()"
            >
                <i class="icon icon-check" /> {{ $t('pollDetails.vote') }}
            </div>

            <div v-show="isVoteProcessing"
                class="answer__overlay-option answer__overlay-option--loader"
            >
                <img src="../../assets/vote-loader.svg"
                    alt="Processing vote"
                >

                <div>{{ processingVoteText }}</div>
            </div>
        </div>

        <div v-if="hasImage || hasMultipleImages || hasVideo"
            class="answer__image"
            @click.stop="showModal()"
        >
            <img
                :src="previewImage || require('@webapp/assets/socios-grey-placeholder.webp')"
                alt="Answer Image"
            >

            <loading-spinner />
        </div>

        <i
            v-if="hasMultipleImages || hasVideo"
            :class="['answer__multi-image', {'icon-multi-image' : hasMultipleImages}, {'icon-videos': hasVideo}]"
        />

        <div class="answer__text">{{ answerText }}</div>
    </div>
</template>

<script>
// 3rd Party Libs
import axios from 'axios';

import {
    mapGetters,
    mapActions,
} from 'vuex';

// Helpers
import apiUrl from '@webapp/helpers/api-url.js';

// Config
import {
    pollClosedStatuses,
} from '@shared/config/poll-statuses.js';

// Shared Modules
import {
    EventNames,
} from '@shared/utils/analytics-keys';

import {
    mimeTypeMixin,
} from '@shared/mixins/mime-type-mixin';

// Components
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

export default {
    name: 'poll-answer',
    components: {
        loadingSpinner,
    },
    mixins: [mimeTypeMixin],
    props: {
        answer: {
            type: Object,
            default: () => ({}),
        },
        hasVoted: {
            type: Boolean,
            default: false,
        },
        hasBalanceToVote: {
            type: Boolean,
            default: false,
        },
        index: {
            type: Number,
            default: 0,
        },
        poll: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            isOverlayVisible: false,
            isVoteProcessing: false,
        };
    },
    computed: {
        ...mapGetters([
            'userDetails',
            'getTranslatedStr',
            'isPartnerApp',
            'partnerAppData',
            'userId',
        ]),

        videoAsset() {
            return this.answer?.images?.find((asset) => this.hasVideoMimeType(asset));
        },

        answerText() {
            return this.getTranslatedStr(this.answer, 'answer', this.answer.answer);
        },
        canVote() {
            return !this.isPollClosed && !this.poll.userVote && this.hasBalanceToVote && !this.hasVoted;
        },

        images() {
            if (!this.answer?.images?.length) {
                return [];
            }

            // Filter out the images that are uploaded for WEB (the large res ones)
            const webImages = this.answer.images.filter((asset) => asset.imageType.type.includes('answer_image_desktop'));

            if (!webImages.length) {
                const allImages = this.answer.images.filter((asset) => !this.hasVideoMimeType(asset));

                // If there are no images, but only videos, return an array with the placeholder image
                return allImages.length ? allImages : [
                    {
                        url: require('@webapp/assets/socios-grey-placeholder.webp'),
                        imageType: {
                            type: 'answer_image_desktop',
                        },
                        mimeType: 'webp',
                    },
                ];
            }

            return webImages;
        },

        previewImage() {
            const desktopPreviewImage = this.images.find((image) => image.imageType.type === 'answer_image_desktop');

            return desktopPreviewImage ? desktopPreviewImage?.url : this.images[0]?.url;
        },

        galleryMedia() {
            // If the answer has a video, only return the video.
            if (this.hasVideo) {
                return [this.videoAsset];
            }

            // Return the images ordered by type
            return this.images.slice().sort((a, b) => (a.imageType.type > b.imageType.type ? 1 : -1));
        },
        hasImage() {
            return this.images.length > 0;
        },
        hasMultipleImages() {
            return this.images.length > 1;
        },
        hasVideo() {
            return this.videoAsset && this.hasImage;
        },
        isPollClosed() {
            return pollClosedStatuses.includes(this.poll.status);
        },
        isPollDisabled() {
            return !this.canVote || this.poll.status === 'scheduled';
        },
        processingVoteText() {
            return this.poll && this.poll.currencyId
                ? this.$t('pollDetails.beingProcessedToBlockchain')
                : this.$t('pollDetails.beingProcessedWithoutBlockchain');
        },
    },
    created() {
        this.detectAnswerClick();
    },
    methods: {
        ...mapActions(['fetchSingleSurvey']),

        detectAnswerClick() {
            // Do not allow clicking if the poll is disabled
            if (this.isPollDisabled) {
                return;
            }

            document.addEventListener('click', (e) => {
                let clickedElement = e.target;
                const answer = document.getElementById(`answer-${this.index}`),
                    cancelButton = document.getElementById(`cancel-vote-${this.index}`);

                do {
                    // If either cancel or the multi-image icon are clicked, do not show the overlay
                    if (e.target.classList.contains('icon-multi-image') || clickedElement === cancelButton) {
                        this.isOverlayVisible = false;

                        return;
                    }

                    // If the answer is clicked, show the overlay
                    if (clickedElement === answer) {
                        this.isOverlayVisible = true;

                        return;
                    }
                    // Go up the DOM
                    clickedElement = clickedElement.parentNode;
                } while (clickedElement);

                // If a click outside the answer is detected, hide the overlay
                this.isOverlayVisible = false;
            });
        },
        showModal() {
            if (this.hasMultipleImages || this.hasVideo) {
                this.$emit('updateModalImages', this.galleryMedia);
            }
        },
        async vote() {
            try {
                this.isVoteProcessing = true;

                await axios.post(`${apiUrl()}/survey/${this.poll.id}/vote`, {
                    answerId: this.answer.id,
                });

                // Mark the answer as voted in order to prevent re-voting
                this.answer.voteStatus = true;

                if (this.isPartnerApp) {
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.PartnerSurveyVote,
                        [
                            {
                                key: 'partner',
                                value: this.partnerAppData.id,
                            },
                            {
                                key: 'user',
                                value: this.userId,
                            },
                            {
                                key: 'survey',
                                value: this.poll.id,
                            },
                        ]
                    );
                }

                await this.fetchSingleSurvey(this.$route.params.id);
            } catch (error) {
                const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                this.$notify({
                    type: 'error',
                    text: fetchErrors.message,
                });
            } finally {
                this.isVoteProcessing = false;
                this.isOverlayVisible = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.answer {
    display: flex;
    gap: 1rem;
    background-color: $concrete-color;
    padding: 2rem 5%;
    margin: 2rem 0;
    border-radius: 1rem;
    font-size: 1.4rem;
    align-items: center;
    position: relative;
    min-height: 8rem;

    @include min-width(md) {
        padding: 2rem 10%;
    }

    @include min-width(lg) {
        padding: 2rem 20%;
        font-size: 1.8rem;
        min-height: 12rem;
    }

    &--is-hoverable {
        cursor: pointer;

        &:hover {
            opacity: 0.8;
        }
    }

    &--is-disabled {
        opacity: 0.3;
    }

    &--is-voted {
        opacity: 1;
        background: $lima-color-opaque;
        font-weight: bold;
    }

    &__image {
        height: 8rem;
        width: 16rem;
        overflow: hidden;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;

        @include min-width(lg) {
            height: 12rem;
            width: 24rem;
        }

        img {
            height: 100%;
            object-fit: cover;
            position: absolute;
            z-index: 1;
        }
    }

    &__text {
        text-align: center;
        word-break: break-all;
        flex: 1;
    }

    &__multi-image {
        font-size: 2.5rem;
        align-self: flex-end;
        margin-left: 0.5rem;

        &:hover {
            cursor: pointer;
            opacity: 0.8;
        }
    }

    &__overlay {
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0;
        background: rgba($cyprus-color, 0.9);
        z-index: 2;
        display: none;
        align-items: center;
        justify-content: center;
        border-radius: 1rem;

        &--is-visible {
            display: flex;
        }

        &-option {
            flex: 1;
            color: $white-color;
            font-weight: bold;
            font-size: 1.4rem;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            @include min-width(lg) {
                font-size: 1.8rem;
            }

            &--loader {
                flex-direction: column;
                font-size: 1.5rem;
                color: $white-color;
                cursor: default;

                img {
                    margin-bottom: 1rem;
                    width: 4.5rem;
                }
            }

            .icon {
                font-size: 3rem;
                margin-right: 2rem;
                border-radius: 100%;
                color: $french-gray-color;
                background: rgba($french-gray-color, 0.1);

                @include min-width(lg) {
                    font-size: 5rem;
                    margin-right: 4rem;
                }
            }

            &--vote {
                .icon {
                    color: $lima-color;
                    background: $lima-color-opaque;
                }
            }
        }
    }
}
</style>